<template>
    <VJoyTemplate v-if="quote" class="quote-overview">
        <template #template-back>
            <NuxtLink to="/">
                <VJoyIconButton icon="arrow-left" variant="ghost" size="medium" />
            </NuxtLink>
        </template>

        <template #template-title>
            <div class="quote-overview_header">
                <div class="quote-overview_header_title">
                    <span data-testid="quote-name">{{ quote.title }}</span>
                    <VJoyBadge :variant="statusVariant" bulletpoint data-testid="quote-status">
                        {{ $t(`projectclient.quote.view.overview.status.${quote.status}`) }}
                    </VJoyBadge>
                </div>

                <div v-if="isQuoteActionable(quote)" class="quote-overview_header_actions">
                    <VJoyButton icon="social-share" variant="ghost" size="xsmall" @click="shareQuote">
                        {{ $t('projectclient.quote.view.overview.actions.share-quote') }}
                    </VJoyButton>
                    <VJoyButton icon="download" variant="ghost" size="xsmall" @click="downloadQuote">
                        {{ $t('projectclient.quote.view.overview.actions.download-quote') }}
                    </VJoyButton>
                </div>
            </div>
        </template>

        <template #template-subtitle>
            {{ $t('projectclient.quote.view.overview.reference.number', [quote.referenceNumber]) }}
        </template>

        <template #template-main>
            <div class="quote-view_body">
                <div class="joy-mb-4">
                    <VJoyHighlight
                        v-if="isQuoteAcceptanceBlockedFor(quote, QuoteAcceptanceBlockingReason.FreelancerVerification)"
                        level="info"
                        display-icon
                        icon="info-circle"
                        data-testid="quote-acceptance-blocked-banner"
                    >
                        <template #highlight-title>
                            {{ $t('projectclient.quote.view.overview.acceptance-blocked-banner.title') }}
                        </template>
                        <p>
                            {{ $t('projectclient.quote.view.overview.acceptance-blocked-banner.description', [quote.freelancer.firstName]) }}
                        </p>
                    </VJoyHighlight>

                    <VJoyHighlight
                        v-else-if="quote.options.isFreelancerVerificationBypassed && !quote.options.acceptanceBlockingReason"
                        level="info"
                        display-icon
                        icon="info-circle"
                        data-testid="quote-pre-authorized-freelancer-banner"
                    >
                        <template #highlight-title>
                            {{ $t('projectclient.quote.view.overview.pre-authorized-freelancer-banner.title') }}
                        </template>
                        <p>
                            {{ $t('projectclient.quote.view.overview.pre-authorized-freelancer-banner.description') }}
                        </p>
                    </VJoyHighlight>

                    <VJoyHighlight
                        v-else-if="quote.options.isFreelancerRecentlyVerified && !quote.options.acceptanceBlockingReason"
                        level="success"
                        display-icon
                        data-testid="quote-freelancer-recently-verified-banner"
                    >
                        <template #highlight-title>
                            {{ $t('projectclient.quote.view.overview.freelancer-recently-verified-banner.title') }}
                        </template>
                        <p>
                            {{
                                $t('projectclient.quote.view.overview.freelancer-recently-verified-banner.description', [quote.freelancer.firstName])
                            }}
                        </p>
                    </VJoyHighlight>
                </div>
                <QuoteTeam :quote="quote" />

                <template v-if="isQuoteFixedPrice(quote)">
                    <QuoteLines v-if="quote.details.quoteLines" :currency="quote.currencyCode" :quote-lines="quote.details.quoteLines" />
                    <AdditionalNotes v-if="quote.details.additionalNotes" :quote="quote" />
                </template>

                <template v-else-if="isQuoteTimeAndMaterials(quote)">
                    <QuoteDetails v-if="quote.details.workRate" :quote="quote" />
                    <PaymentDetails v-if="displayPaymentDetails" :quote="quote" />
                </template>

                <QuoteTermsAndConditions v-if="displayTermsAndConditions" :quote="quote" />
                <AppendixUpload v-if="quote.appendix?.canBeAdded && isQuoteActionable(quote)" :quote="quote" />
            </div>
        </template>

        <template #template-actions>
            <QuoteAdminActions />
        </template>

        <template #template-sidebar>
            <QuoteSummary :quote="quote" :is-fees-info-visible="isFeesInfoVisible" :show-add-phone-number="showAddPhoneNumber" />
        </template>
    </VJoyTemplate>
</template>

<script setup lang="ts">
    import {computed, onMounted} from 'vue';
    import {QuoteAdminActions} from '@features/project-view/ui';
    import {useClientQuoteViewStore} from '@features/quote/stores';
    import {navigateTo} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {useTranslation, useAmplitudeSessionReplay, useDataTracking} from '#imports';
    import {copyToClipboard} from '@malt/dom';
    import {useFeatureFlag} from '@malt/feature-flags';
    import {VJoyBadge, VJoyButton, VJoyHighlight, VJoyIconButton, VJoyTemplate, pushVJoySnackbar} from '@maltjoy/core-vue';
    import {QuoteAcceptanceBlockingReason, QuoteStatus} from '~/openapi';
    import {QuoteDetails, QuoteTeam, QuoteTermsAndConditions, QuoteSummary, AdditionalNotes, AppendixUpload} from './layout-sections';
    import {
        isQuoteAcceptanceBlockedFor,
        isQuoteActionable,
        isQuoteFixedPrice,
        isQuotePrepayment,
        isQuoteTimeAndMaterials,
        isQuoteTimeAndMaterialsWithAtLeastNLots,
    } from '../utils';
    import {QuoteLines} from './layout-sections/FixedPriceDetails';
    import PaymentDetails from './layout-sections/PaymentDetails.vue';

    const {t} = useTranslation();
    const {track} = useDataTracking();

    const clientViewStore = useClientQuoteViewStore();
    const {quote, isFeesInfoVisible, showAddPhoneNumber} = storeToRefs(clientViewStore);

    const displayTermsAndConditions = computed(() => quote.value?.clientTerms || quote.value?.projectTerms || quote.value?.freelancerTerms);

    const displayPaymentDetails = computed(
        () =>
            useFeatureFlag('PROJ-3121-quote-validation-payment-details').value &&
            isQuoteTimeAndMaterials(quote.value) &&
            isQuotePrepayment(quote.value) &&
            isQuoteTimeAndMaterialsWithAtLeastNLots(2, quote.value),
    );

    function downloadQuote() {
        if (quote.value?.pdfUrl) {
            navigateTo('/' + quote.value?.pdfUrl, {
                open: {
                    target: '_blank',
                },
            });
        }
    }

    function shareQuote() {
        copyToClipboard(window.location.href);
        pushVJoySnackbar({props: {message: t('projectclient.quote.view.overview.actions.share-quote.copy.success'), level: 'success'}});
    }

    const statusVariant = computed(() => {
        switch (quote.value?.status) {
            case QuoteStatus.Submitted:
                return 'pink';
            case QuoteStatus.Canceled:
                return 'red';
            case QuoteStatus.Denied:
                return 'red';
            case QuoteStatus.Expired:
                return 'teal';
            case QuoteStatus.Accepted:
                return 'green';
            case QuoteStatus.AcceptanceBlocked:
                return 'orange';
            default:
                return 'gray';
        }
    });

    onMounted(() => {
        if (quote.value) {
            track('client_quote_page_viewed', {quote_id: quote.value.id, mission_correlation_id: quote.value.correlationId});
        }
    });
    useAmplitudeSessionReplay(() => !quote.value?.options.acceptanceBlockingReason);
</script>

<style lang="scss" scoped>
    .quote-overview {
        &_header {
            display: flex;
            justify-content: space-between;

            .joy-badge {
                min-width: fit-content;
            }

            &_title {
                display: flex;
                gap: var(--joy-core-spacing-2);
            }

            &_actions {
                display: flex;
                flex-wrap: wrap;
            }
        }
    }
</style>
